import { useEffect, useRef, useState } from 'react';
import {
  GENERATION_STATUSES,
  isGenerationRequestStatus,
  isTerminalStatus,
  statusRank,
} from '../../lib/pipeline';
import type { GenerationRequestStatus } from '../../types/api';
import { generationEventsUrl, mintSseToken } from '../generation/generationApi';

/** Base gap before a reconnect. Multiplied by the attempt number. */
const RECONNECT_DELAY_MS = 2000;

/** Give up after this many failed reconnects and tell the user to reload. */
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Health of the progress stream, separate from pipeline status: the run can be
 * fine while the connection is not.
 */
export type StreamConnection = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'lost';

export interface GenerationProgressResult {
  /** Latest stage the stream reported, or the status the page loaded with. */
  status: GenerationRequestStatus;
  /** Optional note from the server for the current stage. */
  detail: string | null;
  connection: StreamConnection;
}

/** Live state, tagged with the request and the phase it belongs to. */
interface LiveProgress {
  requestId: string;
  /**
   * The status this subscription was opened on — the run's phase, as far as
   * this screen is concerned. A request streams more than once (copy, then the
   * visual phase, then a `revising` detour each time one is asked for), and the
   * last frame of one phase says nothing true about the next.
   */
  phase: GenerationRequestStatus;
  status: GenerationRequestStatus;
  detail: string | null;
  connection: StreamConnection;
}

/** One decoded SSE frame. */
interface ProgressFrame {
  status: GenerationRequestStatus;
  detail: string | null;
}

/**
 * Decodes an SSE `data` payload.
 *
 * The frame is untrusted input, so the status is narrowed rather than cast, and
 * anything unreadable falls back to `eventName` — which is itself the status,
 * per the event-naming convention. That keeps a malformed body from stalling
 * the tracker.
 */
function parseFrame(data: unknown, eventName: GenerationRequestStatus): ProgressFrame {
  if (typeof data !== 'string') return { status: eventName, detail: null };

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return { status: eventName, detail: null };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { status: eventName, detail: null };
  }

  const body = parsed as { status?: unknown; detail?: unknown };
  return {
    status: isGenerationRequestStatus(body.status) ? body.status : eventName,
    detail: typeof body.detail === 'string' && body.detail ? body.detail : null,
  };
}

/**
 * Subscribes to one request's pipeline progress over SSE.
 *
 * Resilience (SRS §6):
 * - The subscription is authenticated with a short-lived token minted through
 *   an authenticated call and passed as a query param. The Supabase session
 *   token never appears in a URL.
 * - On a drop the browser's own reconnect would replay the same 5-minute token,
 *   so the `EventSource` is closed and reopened against a freshly minted one,
 *   with a linear backoff and a hard attempt cap.
 * - A refresh mid-run is not a special case: the effect re-runs on mount and
 *   resubscribes, and the backend resumes from its checkpoint rather than
 *   restarting the pipeline.
 *
 * `onTerminal` fires once the run reaches a status the server sends nothing
 * further about — either waiting state, `complete`, `failed` or `rejected` —
 * which is the caller's cue to refetch the request. It carries that frame's
 * `detail`, which is how a revision that could not be applied gets its
 * explanation onto the review page: the row goes back to the status it came
 * from and the sentence rides the closing frame. It is read through a ref, so a
 * caller passing an inline function never restarts the stream.
 *
 * A request subscribes more than once: a carousel for its copy phase and again
 * when copy selection moves it to `designing`, and either kind again whenever a
 * revision puts it back to `revising`. None of those is a special case here —
 * `initialStatus` goes non-terminal again, the effect re-runs and mints a fresh
 * token.
 */
export function useGenerationProgress(
  businessId: string | null,
  requestId: string | null,
  /** Status the request was loaded with. Null while it is still loading. */
  initialStatus: GenerationRequestStatus | null,
  onTerminal: (status: GenerationRequestStatus, detail: string | null) => void,
): GenerationProgressResult {
  const [live, setLive] = useState<LiveProgress | null>(null);
  const terminalRef = useRef(onTerminal);
  // Read through a ref for the same reason as `onTerminal`: a status change
  // pushed by a refetch must not tear down and re-mint a live subscription.
  const initialStatusRef = useRef(initialStatus);

  useEffect(() => {
    terminalRef.current = onTerminal;
    initialStatusRef.current = initialStatus;
  }, [onTerminal, initialStatus]);

  // Nothing to listen to once the pipeline has stopped: the server closes the
  // stream at a terminal status, and reopening it would only churn tokens.
  const shouldStream =
    businessId !== null &&
    requestId !== null &&
    initialStatus !== null &&
    !isTerminalStatus(initialStatus);

  useEffect(() => {
    if (!shouldStream || businessId === null || requestId === null) return;

    // The phase this subscription belongs to: copy, the visual phase after a
    // selection, or a `revising` detour. Captured once, so every frame it
    // delivers is labelled with the state the run was in when it opened.
    const phase = initialStatusRef.current ?? 'queued';

    let cancelled = false;
    let settled = false;
    let attempts = 0;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function patch(changes: Partial<Omit<LiveProgress, 'requestId' | 'phase'>>) {
      if (requestId === null) return;
      setLive((current) =>
        current !== null && current.requestId === requestId && current.phase === phase
          ? { ...current, ...changes }
          : {
              requestId,
              phase,
              status: changes.status ?? phase,
              detail: changes.detail ?? null,
              connection: changes.connection ?? 'connecting',
            },
      );
    }

    function scheduleReconnect() {
      if (cancelled || settled) return;

      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        patch({ connection: 'lost' });
        return;
      }

      attempts += 1;
      patch({ connection: 'reconnecting' });
      timer = setTimeout(() => {
        timer = null;
        void open();
      }, RECONNECT_DELAY_MS * attempts);
    }

    async function open() {
      if (cancelled || settled || businessId === null || requestId === null) return;

      let token: string;
      try {
        // A fresh token per attempt: the previous one may already have expired.
        const minted = await mintSseToken(businessId, requestId);
        token = minted.token;
      } catch {
        // Could be a transient 5xx or an offline window; the backoff decides.
        scheduleReconnect();
        return;
      }

      if (cancelled || settled) return;

      const stream = new EventSource(generationEventsUrl(requestId, token));
      source = stream;

      function close() {
        stream.close();
        if (source === stream) source = null;
      }

      stream.onopen = () => {
        attempts = 0;
        patch({ connection: 'open' });
      };

      for (const status of GENERATION_STATUSES) {
        stream.addEventListener(status, (event: Event) => {
          const frame = parseFrame((event as MessageEvent<unknown>).data, status);
          if (cancelled) return;

          // A delivered frame proves the stream is healthy again.
          attempts = 0;
          patch({ status: frame.status, detail: frame.detail, connection: 'open' });

          if (isTerminalStatus(frame.status)) {
            settled = true;
            close();
            terminalRef.current(frame.status, frame.detail);
          }
        });
      }

      stream.onerror = () => {
        // Fires on a dropped connection and on the normal close after a
        // terminal frame; only the former is worth reconnecting for.
        close();
        scheduleReconnect();
      };
    }

    void open();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      source?.close();
    };
  }, [businessId, requestId, shouldStream]);

  const fallbackStatus = initialStatus ?? 'queued';
  // The last frame of a finished phase outlives its subscription: a carousel
  // restarting into `designing`, or any run detouring into `revising`, would
  // otherwise briefly read as the state it just left. Live progress is only
  // believed while it belongs to the phase the request itself reports.
  const isCurrentPhase =
    live !== null && live.requestId === requestId && live.phase === fallbackStatus;
  // And only ever forwards: a frame is believed once it is at least as far
  // along the track as the status the request itself reports.
  const isLiveCurrentPhase =
    isCurrentPhase && statusRank(live.status) >= statusRank(fallbackStatus);

  return {
    status: isLiveCurrentPhase ? live.status : fallbackStatus,
    detail: isLiveCurrentPhase ? live.detail : null,
    connection:
      isCurrentPhase && shouldStream ? live.connection : shouldStream ? 'connecting' : 'idle',
  };
}
